import loadable from '@loadable/component';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useDeviceType, useOnResizeDebounce, MOBILE_SCREEN } from '@travel/traveler-core/hooks';
import { PROVIDER_LIST_TYPE } from '@travel/traveler-core/types/providerInformation';
import { toSEOSearchString } from '@travel/traveler-core/utils/seo';

import CouponAccordion from 'components/CouponAccordion';
import { OFFER_TYPE } from 'constants/salesPromotion';
import paths from 'core/universalRouter/paths';
import { getIsFetching as getIsFetchingPlanList } from 'store/providerPlanList/selectors';
import { getIsFetching as getIsFetchingRoomList } from 'store/providerRatePlan/selectors';
import { getCouponDetails } from 'store/salesPromotion/selectors';
import ListingTypeSelectorTabList from '../ListingTypeSelectorTabList';
import RoomList from '../RoomList';
import SearchForm from '../SearchForm';

import { replaceLocation } from 'store/__router/actions';

import { ProviderInfoListTypes, ProviderInfoQuery } from 'ProviderInformation-Types';
import { isClient } from 'store/utils';
import { URLParams } from '../../resolver';

import { getLimitationsForCurrentMarket } from 'store/markets/selectors';

import styles from './roomAndPlanList.module.scss';

const PlanList = loadable(
  () =>
    import(
      /* webpackMode: "lazy" */
      /* webpackFetchPriority: "low" */
      /* webpackChunkName: "component-PlanList" */
      '../PlanList'
    ),
  { ssr: false },
);

type Props = {
  query: ProviderInfoQuery;
  params: URLParams;
  providerInfoCanonicalUrl: string;
  shouldLazyLoad?: boolean;
};

const TAB_EXISTENCE_REFRESH_INTERVAL = 100; // Refresh Interval time to update the width till the element is fully loaded

function RoomAndPlanList(props: Props) {
  const { query, params, providerInfoCanonicalUrl, shouldLazyLoad = false } = props;

  const deviceType = useDeviceType();
  const isSP = deviceType === MOBILE_SCREEN;

  const dispatch = useDispatch();
  const [activeTabId, setActiveTabId] = useState<string>(
    query.listType ? query.listType : PROVIDER_LIST_TYPE.ROOM_LIST,
  );
  const [isPlanListVisited, setIsPlanListVisited] = useState<boolean>(
    query.listType === PROVIDER_LIST_TYPE.PLAN_LIST,
  );
  const [isAutoOpenDatePicker, setIsAutoOpenDatePicker] = useState(false);

  const currentCoupon = useSelector(getCouponDetails(query.salesPromotionId || ''));
  const isFetchingRoomList = useSelector(getIsFetchingRoomList);
  const isFetchingPlanList = useSelector(getIsFetchingPlanList);
  const isFetching =
    activeTabId === PROVIDER_LIST_TYPE.ROOM_LIST ? isFetchingRoomList : isFetchingPlanList;
  // tab scroll logics
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const prevActiveTabIdRef = useRef(activeTabId);
  const tagFilterAndTabListWrapperRef = useRef<HTMLDivElement | null>(null);
  const roomPlanTabListRef = useRef<HTMLDivElement>(null);
  const [tabListWidth, setTabListWidth] = useState<number>(0);

  useEffect(() => {
    if (
      anchorRef.current &&
      prevActiveTabIdRef.current !== activeTabId &&
      tagFilterAndTabListWrapperRef.current
    ) {
      const offsetTop = anchorRef.current?.offsetTop;
      const tagFilterAndTabListHeight = tagFilterAndTabListWrapperRef.current?.offsetHeight;
      window.scrollTo(0, offsetTop - tagFilterAndTabListHeight);
    }
  }, [activeTabId]);

  const updateURL = useCallback(
    search => {
      // to update URL on query changed
      dispatch(
        replaceLocation({
          pathname: paths.providerInfo.pathResolver(providerInfoCanonicalUrl),
          search,
        }),
      );

      if (anchorRef.current && isClient && tagFilterAndTabListWrapperRef.current) {
        const offsetTop = anchorRef.current?.offsetTop;
        const tagFilterAndTabListHeight = tagFilterAndTabListWrapperRef.current?.offsetHeight;
        window.scrollTo(0, offsetTop - tagFilterAndTabListHeight);
      }
    },
    [dispatch, providerInfoCanonicalUrl],
  );

  const onMoreDateClick = useCallback(() => {
    setIsAutoOpenDatePicker(true);
  }, []);

  const updateTabListWidth = () => {
    if (roomPlanTabListRef.current && window) {
      const width = roomPlanTabListRef.current?.getBoundingClientRect().width || 0;
      const computedStyle = window.getComputedStyle(roomPlanTabListRef.current);
      const marginLeft = computedStyle ? parseInt(computedStyle.marginLeft, 10) || 0 : 0;
      const marginRight = computedStyle ? parseInt(computedStyle.marginRight, 10) || 0 : 0;
      setTabListWidth(width + marginLeft + marginRight);
    }
  };

  useLayoutEffect(() => {
    if (!roomPlanTabListRef.current) {
      const tabListInterval = setInterval(() => {
        if (roomPlanTabListRef.current) {
          clearInterval(tabListInterval);
          updateTabListWidth();
        }
      }, TAB_EXISTENCE_REFRESH_INTERVAL);
      return () => clearInterval(tabListInterval);
    }

    updateTabListWidth();
  }, []);

  useOnResizeDebounce(updateTabListWidth);

  const limitations = useSelector(getLimitationsForCurrentMarket);
  const onRemoveCoupon = useCallback(() => {
    query['isCouponSpecified'] = undefined;
    updateURL?.(toSEOSearchString({ ...query, salesPromotionId: undefined }, limitations));
  }, [updateURL, query]);

  const handleChangeTabs = useCallback((tabId: string) => {
    const url = new URL(window.location.toString());

    if (url.searchParams.get('listType') !== tabId && isClient) {
      tabId === PROVIDER_LIST_TYPE.PLAN_LIST && setIsPlanListVisited(true);
      url.searchParams.set('listType', tabId);
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const listingTypeSelectorTabList = (
    <ListingTypeSelectorTabList
      innerRef={roomPlanTabListRef}
      activeTabId={activeTabId}
      setActiveTabId={setActiveTabId}
      prevActiveTabIdRef={prevActiveTabIdRef}
      setIsAutoOpenDatePicker={setIsAutoOpenDatePicker}
      onChangeTabs={handleChangeTabs}
    />
  );

  const roomsAndPlanListSearchForm = (
    <SearchForm
      listType={activeTabId as ProviderInfoListTypes}
      pageIdentifier={params.pageIdentifier}
      query={query}
      isFetching={isFetching}
      updateURL={updateURL}
      isAutoOpenDatePicker={isAutoOpenDatePicker}
      setIsAutoOpenDatePicker={setIsAutoOpenDatePicker}
      listingTypeSelectorTabList={listingTypeSelectorTabList}
      tagFilterAndTabListRef={tagFilterAndTabListWrapperRef}
      tabListWidth={tabListWidth}
    />
  );

  const listClassName = {
    className: styles.contentBox,
    emptyClassName: styles.emptyContentBox,
  };
  const hiddenClassName = {
    className: styles.hiddenBox,
    emptyClassName: styles.hiddenBox,
  };

  return (
    <div data-testid="roomAndPlanList-wrapper">
      {isSP ? (
        <>{roomsAndPlanListSearchForm}</>
      ) : (
        <>
          <div ref={anchorRef} />
          <div className={styles.sticky}>
            <div className={styles.innerSticky}>{roomsAndPlanListSearchForm}</div>
          </div>
        </>
      )}
      {/* sales promotion */}
      {query.salesPromotionId && (
        <>
          {currentCoupon?.offerType !== OFFER_TYPE.DIRECT && (
            <CouponAccordion
              couponId={query.salesPromotionId}
              couponContentClassName={styles.couponContent}
              className={styles.couponWrapper}
              onRemoveCoupon={onRemoveCoupon}
            />
          )}
        </>
      )}

      <RoomList
        pageIdentifier={params.pageIdentifier}
        providerInfoCanonicalUrl={providerInfoCanonicalUrl}
        query={query}
        {...(activeTabId === PROVIDER_LIST_TYPE.ROOM_LIST ? listClassName : hiddenClassName)}
        moreDateClick={onMoreDateClick}
      />
      {(!shouldLazyLoad || isPlanListVisited) && (
        <PlanList
          pageIdentifier={params.pageIdentifier}
          providerInfoCanonicalUrl={providerInfoCanonicalUrl}
          query={query}
          {...(activeTabId === PROVIDER_LIST_TYPE.PLAN_LIST ? listClassName : hiddenClassName)}
          moreDateClick={onMoreDateClick}
        />
      )}
    </div>
  );
}

export default React.memo(RoomAndPlanList);
